<script setup lang="ts">
import { computed } from 'vue';
import { UiCheckbox, UiIcon, UiInfoBox, UiInputField, UiNumberField, UiSelect, UiTextarea, UiText } from '@shopexperts/shared';
import type { PageSchemaField, PageSchemaFieldType } from '@shopexperts/shared';
import { formatMetafieldReference, getPageFieldTypeDefinition, PAGE_FIELD_TYPE_OPTIONS } from '../../field-types';
import MetafieldKeyCopyButton from '../shared/MetafieldKeyCopyButton.vue';

const props = defineProps<{
  field: PageSchemaField | null;
}>();

const emit = defineEmits<{
  updateField: [patch: Partial<PageSchemaField>];
  updateOptions: [value: string];
}>();

function optionsText(field: PageSchemaField | null): string {
  return (field?.options || []).join('\n');
}

function hasSearchRole(role: 'label' | 'image'): boolean {
  return Boolean(props.field?.search_role?.includes(role));
}

function updateSearchRole(role: 'label' | 'image', enabled: boolean) {
  const roles = new Set(props.field?.search_role || []);
  if (enabled) {
    roles.add(role);
  } else {
    roles.delete(role);
  }
  emit('updateField', { search_role: Array.from(roles) });
}

function isEntityFieldType(type: PageSchemaFieldType): boolean {
  return type === 'partner_badge' || type === 'service_category' || type === 'service_subcategory';
}

function defaultTextValue(field: PageSchemaField): string {
  return typeof field.default_value === 'string' ? field.default_value : '';
}

function defaultNumberValue(field: PageSchemaField): string {
  return typeof field.default_value === 'number' && Number.isFinite(field.default_value) ? String(field.default_value) : '';
}

function updateTextDefault(value: unknown) {
  emit('updateField', { default_value: String(value || '') || null });
}

function updateNumberDefault(value: unknown) {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) {
    emit('updateField', { default_value: null });
    return;
  }
  const parsed = Number(trimmed);
  emit('updateField', { default_value: Number.isFinite(parsed) ? parsed : null });
}

const searchRolePriorityValue = computed({
  get: () => props.field?.search_role_priority == null ? '' : String(props.field.search_role_priority),
  set: (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      emit('updateField', { search_role_priority: undefined });
      return;
    }
    const parsed = Number(trimmed);
    emit('updateField', { search_role_priority: Number.isFinite(parsed) ? parsed : undefined });
  },
});
</script>

<template>
  <aside class="min-h-0 w-[380px] shrink-0 overflow-y-auto bg-white p-5">
    <UiText as="h2" variant="h6" text="Field definition" class="mb-4 uppercase tracking-wide text-se-gray-700" />

    <UiInfoBox v-if="!props.field" variant="warning">
      <UiText as="p" variant="p-small" text="Add or select a field to edit its definition." />
    </UiInfoBox>

    <div v-else class="flex flex-col gap-4">
      <div class="flex items-center gap-2">
        <UiIcon :name="getPageFieldTypeDefinition(props.field.type).icon" class="h-5 w-5 text-se-gray-700" />
        <UiText as="span" variant="p-small" :text="props.field.label" />
      </div>

      <div class="flex flex-col gap-1">
        <UiText as="label" variant="label" text="Key" />
        <UiInputField :model-value="props.field.key" @update:model-value="emit('updateField', { key: String($event) })" />
        <MetafieldKeyCopyButton :field-key="props.field.key" />
      </div>

      <div class="flex flex-col gap-1">
        <UiText as="label" variant="label" text="Template reference" />
        <UiInputField :model-value="formatMetafieldReference(props.field.key)" readonly />
      </div>

      <div class="flex flex-col gap-1">
        <UiText as="label" variant="label" text="Label" />
        <UiInputField :model-value="props.field.label" @update:model-value="emit('updateField', { label: String($event) })" />
      </div>

      <div class="flex flex-col gap-1">
        <UiText as="label" variant="label" text="Type" />
        <UiSelect
          :model-value="props.field.type"
          :options="PAGE_FIELD_TYPE_OPTIONS"
          @update:model-value="emit('updateField', { type: $event as PageSchemaFieldType })"
        />
      </div>

      <UiCheckbox
        :model-value="Boolean(props.field.required)"
        label="Required"
        @update:model-value="emit('updateField', { required: Boolean($event) })"
      />

      <div class="flex flex-col gap-3 border-t border-se-gray-200 pt-4">
        <div>
          <UiText as="h3" variant="label" text="Search roles" />
          <UiText as="p" variant="caption-normal" class="mt-1 text-se-gray-700" text="Choose how this field can represent pages in search results." />
        </div>
        <UiCheckbox
          :model-value="hasSearchRole('label')"
          label="Use as search label"
          @update:model-value="updateSearchRole('label', Boolean($event))"
        />
        <UiCheckbox
          :model-value="hasSearchRole('image')"
          label="Use as search image"
          @update:model-value="updateSearchRole('image', Boolean($event))"
        />
        <div class="flex flex-col gap-1">
          <UiText as="label" variant="label" text="Search priority" />
          <UiNumberField v-model="searchRolePriorityValue" placeholder="1" :min="1" :max="1000" />
        </div>
      </div>

      <div v-if="getPageFieldTypeDefinition(props.field.type).supportsOptions" class="flex flex-col gap-1">
        <UiText as="label" variant="label" text="Options" />
        <UiTextarea :model-value="optionsText(props.field)" :rows="5" @update:model-value="emit('updateOptions', String($event))" />
      </div>

      <div class="flex flex-col gap-1">
        <UiText as="label" variant="label" text="Default value" />
        <UiCheckbox
          v-if="props.field.type === 'boolean'"
          :model-value="Boolean(props.field.default_value)"
          label="Default enabled"
          @update:model-value="emit('updateField', { default_value: Boolean($event) })"
        />
        <UiNumberField
          v-else-if="props.field.type === 'number' || isEntityFieldType(props.field.type)"
          :model-value="defaultNumberValue(props.field)"
          :placeholder="isEntityFieldType(props.field.type) ? 'Optional entity id' : 'Optional number'"
          @update:model-value="updateNumberDefault"
        />
        <UiSelect
          v-else-if="props.field.type === 'select'"
          :model-value="defaultTextValue(props.field)"
          :options="(props.field.options || []).map((option) => ({ label: option, value: option }))"
          @update:model-value="emit('updateField', { default_value: $event || null })"
        />
        <UiInfoBox v-else-if="props.field.type === 'image'" variant="warning">
          <UiText as="p" variant="p-small" text="Image fields do not support a default value in this editor yet." />
        </UiInfoBox>
        <UiInputField
          v-else
          :model-value="defaultTextValue(props.field)"
          placeholder="Optional default"
          @update:model-value="updateTextDefault"
        />
      </div>
    </div>
  </aside>
</template>
